import React from 'react';

import { Check, Close } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import SectionHeader, {
	type SectionHeaderProps,
} from '@ivy/components/molecules/SectionHeader';
import { OPTIONS_ACEP_POLICIES } from '@ivy/constants/acep';
import { gql, type FragmentType, getFragmentData } from '@ivy/gql/types';

import useAcepSectionTooltip from './useAcepSectionTooltip';

const AcepPolicies_OrgFDoc = gql(/* GraphQL */ `
	fragment AcepPolicies_Org on organization {
		id
		surveys: acep_org_surveys(
			where: { accepted_at: { _is_null: false } }
			order_by: [{ accepted_at: desc_nulls_last }]
			limit: 1
		) {
			id
			acepPolicies: acep_policies
		}
	}
`);

export interface AcepPoliciesProps
	extends Omit<SectionHeaderProps, 'title' | 'noBackground'> {
	org: FragmentType<typeof AcepPolicies_OrgFDoc>;
}

const AcepPolicies = ({ org: rawOrg, ...props }: AcepPoliciesProps) => {
	const tooltip = useAcepSectionTooltip();
	const org = getFragmentData(AcepPolicies_OrgFDoc, rawOrg);

	const survey = org.surveys[0];

	return (
		<SectionHeader
			noBackground={!survey}
			title='Adherence to ACEP Policies'
			tooltipContent={tooltip}
			{...props}
		>
			{!survey?.acepPolicies.length ||
			survey?.acepPolicies.includes('Prefer not to answer') ? (
				<Typography variant='body1' color='text.icon' fontStyle='italic'>
					There is nothing here yet.
				</Typography>
			) : (
				<Stack spacing={3}>
					{OPTIONS_ACEP_POLICIES.slice(0, OPTIONS_ACEP_POLICIES.length - 1).map(
						(el) => (
							<Box
								key={el.label}
								sx={{
									display: {
										sm: 'flex',
									},
									alignItems: {
										sm: 'start',
									},
								}}
							>
								<Box
									sx={{
										mr: 1,
									}}
								>
									{survey.acepPolicies.includes(el.label) ? (
										<Check
											sx={{
												color: 'secondary.main',
											}}
										/>
									) : (
										<Close
											sx={{
												color: 'text.disabled',
											}}
										/>
									)}
								</Box>
								<Typography variant='body1'>
									<RouteLink
										openInNewTab
										to={el.url}
										sx={{
											whiteSpace: 'pre-wrap',
											color: survey.acepPolicies.includes(el.label)
												? undefined
												: 'text.disabled',
										}}
									>
										{el.label}
									</RouteLink>
								</Typography>
							</Box>
						),
					)}
				</Stack>
			)}
		</SectionHeader>
	);
};

export default AcepPolicies;
